<p-toast></p-toast>

<app-page-shell>
  <app-page-hero
    eyebrow="POSM Setup"
    title="POSM Configuration"
    subtitle="Maintain project-independent POSM labels, workflows, templates, rates, evidence fields, and approval rules.">
    <div actions>
      <p-button label="Executions" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/posm/executions"></p-button>
      <p-button label="Install Defaults" icon="pi pi-download" severity="secondary" [outlined]="true" [loading]="loading()" (click)="seedDefaults()"></p-button>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="loading()" (click)="load()"></p-button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

  <p-tabs [value]="activeTab()" (valueChange)="setActiveTab($event)" class="block mt-4">
    <p-tablist>
      @for (tab of setupTabs; track tab.id) {
        <p-tab [value]="tab.id">
          <span class="flex align-items-center gap-2 white-space-nowrap">
            <i [class]="tab.icon"></i>
            <span>{{ tab.title }}</span>
          </span>
        </p-tab>
      }
    </p-tablist>

    <p-tabpanels>
      @for (tab of setupTabs; track tab.id) {
        <p-tabpanel [value]="tab.id">
          @if (tab.id === 'retailers') {
            <app-page-data-panel
              title="Retailers / Store Chains"
              subtitle="Tenant-owned retailer masters used to classify POSM store locations. They are not global companies or CRM clients."
              [resultText]="retailers().length + ' retailers'">
              <div class="grid mb-3">
                <div class="col-12 md:col-8">
                  <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                    <p-iftalabel>
                      <input id="posmRetailerName" pInputText [ngModel]="retailerDraft().name" (ngModelChange)="updateRetailerName($event)" class="w-full">
                      <label for="posmRetailerName">Retailer / Store Chain Name</label>
                    </p-iftalabel>
                  </p-inputGroup>
                </div>
                <div class="col-12 md:col-2">
                  <div class="h-full flex align-items-center justify-content-between surface-50 border-1 surface-border border-round px-3 py-2">
                    <span class="text-sm font-medium text-900">Active</span>
                    <p-toggleswitch [ngModel]="retailerDraft().active" (ngModelChange)="updateRetailerActive($event)"></p-toggleswitch>
                  </div>
                </div>
                <div class="col-12 md:col-2 flex align-items-center justify-content-end gap-2">
                  @if (retailerDraft().id) {
                    <p-button label="New" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="resetRetailerDraft()"></p-button>
                  }
                  <p-button label="Save" icon="pi pi-check" [loading]="loading()" (click)="saveRetailer()"></p-button>
                </div>
              </div>

              <p-table [value]="retailers()" [loading]="loading()" styleClass="p-datatable-sm no-borders">
                <ng-template pTemplate="header">
                  <tr><th>Retailer / Store Chain</th><th>Status</th><th>Updated</th><th class="text-right">Action</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td class="font-medium text-900">{{ row.name }}</td>
                    <td><p-tag [value]="row.active === false ? 'Inactive' : 'Active'" [severity]="row.active === false ? 'secondary' : 'success'"></p-tag></td>
                    <td class="text-600">{{ row.updatedAt ? (row.updatedAt | date:'dd/MM/yyyy') : '-' }}</td>
                    <td class="text-right"><div class="flex justify-content-end gap-2"><p-button icon="pi pi-pencil" severity="secondary" [outlined]="true" (click)="editRetailer(row)"></p-button><p-button [icon]="row.active === false ? 'pi pi-check-circle' : 'pi pi-ban'" [severity]="row.active === false ? 'success' : 'danger'" [outlined]="true" (click)="toggleRetailer(row)"></p-button></div></td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage"><tr><td colspan="4" class="text-center p-4 text-500">No retailers / store chains configured.</td></tr></ng-template>
              </p-table>
            </app-page-data-panel>
          } @else {
          <div class="grid">
            @for (group of tab.groups; track group.type) {
              <div class="col-12">
                <app-page-data-panel
                  [title]="group.title"
                  [subtitle]="group.subtitle"
                  [resultText]="activeRows(group.type) + ' active / ' + rows(group.type).length + ' total'">
                  @if (drafts[group.type]; as draft) {
                    <div class="grid mb-3">
                      <div class="col-12 md:col-4">
                        <p-inputGroup>
                          <p-inputGroupAddon><i [class]="group.icon"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <input [id]="'posmSetupName' + group.type" pInputText [(ngModel)]="draft.name" class="w-full">
                            <label [for]="'posmSetupName' + group.type">Name</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-12 md:col-2">
                        <p-inputGroup>
                          <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <input [id]="'posmSetupCode' + group.type" pInputText [(ngModel)]="draft.code" class="w-full">
                            <label [for]="'posmSetupCode' + group.type">Code</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-12 md:col-2">
                        <p-inputGroup>
                          <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <input [id]="'posmSetupCategory' + group.type" pInputText [(ngModel)]="draft.category" class="w-full">
                            <label [for]="'posmSetupCategory' + group.type">Category</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-6 md:col-2">
                        <p-inputGroup>
                          <p-inputGroupAddon>v</p-inputGroupAddon>
                          <p-iftalabel>
                            <p-inputNumber [inputId]="'posmSetupVersion' + group.type" [(ngModel)]="draft.versionNumber" [min]="1" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                            <label [for]="'posmSetupVersion' + group.type">Version</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-6 md:col-2">
                        <p-inputGroup>
                          <p-inputGroupAddon><i class="pi pi-sort-numeric-down"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <p-inputNumber [inputId]="'posmSetupSort' + group.type" [(ngModel)]="draft.sortOrder" [min]="0" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                            <label [for]="'posmSetupSort' + group.type">Order</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-12 md:col-4">
                        <p-inputGroup>
                          <p-inputGroupAddon><i class="pi pi-calendar-plus"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <p-datePicker [inputId]="'posmSetupEffectiveFrom' + group.type" [(ngModel)]="draft.effectiveFrom" appendTo="body" [showIcon]="true" styleClass="w-full"></p-datePicker>
                            <label [for]="'posmSetupEffectiveFrom' + group.type">Effective From</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-12 md:col-4">
                        <p-inputGroup>
                          <p-inputGroupAddon><i class="pi pi-calendar-times"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <p-datePicker [inputId]="'posmSetupEffectiveTo' + group.type" [(ngModel)]="draft.effectiveTo" appendTo="body" [showIcon]="true" styleClass="w-full"></p-datePicker>
                            <label [for]="'posmSetupEffectiveTo' + group.type">Effective To</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-12 md:col-4">
                        <div class="h-full flex align-items-center justify-content-between surface-50 border-1 surface-border border-round px-3 py-2">
                          <div>
                            <div class="text-sm font-medium text-900">Active</div>
                            <div class="text-xs text-500">Available to execution workspaces</div>
                          </div>
                          <p-toggleswitch [(ngModel)]="draft.isActive"></p-toggleswitch>
                        </div>
                      </div>

                      <div class="col-12 md:col-6">
                        <p-inputGroup>
                          <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <textarea [id]="'posmSetupDescription' + group.type" pTextarea rows="3" [(ngModel)]="draft.description" class="w-full"></textarea>
                            <label [for]="'posmSetupDescription' + group.type">Description</label>
                          </p-iftalabel>
                        </p-inputGroup>
                      </div>

                      <div class="col-12 md:col-6">
                        <p-inputGroup>
                          <p-inputGroupAddon><i class="pi pi-code"></i></p-inputGroupAddon>
                          <p-iftalabel>
                            <textarea [id]="'posmSetupConfig' + group.type" pTextarea rows="3" [(ngModel)]="draft.configJson" class="w-full font-monospace"></textarea>
                            <label [for]="'posmSetupConfig' + group.type">Config JSON</label>
                          </p-iftalabel>
                        </p-inputGroup>
                        @if (group.configHint) {
                          <div class="text-xs text-500 mt-1">{{ group.configHint }}</div>
                        }
                      </div>

                      @if (group.type === 'EXCEL_TEMPLATE') {
                        <div class="col-12">
                          <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-table"></i></p-inputGroupAddon>
                            <p-iftalabel>
                              <textarea [id]="'posmSetupMapping' + group.type" pTextarea rows="3" [(ngModel)]="draft.mappingJson" class="w-full font-monospace"></textarea>
                              <label [for]="'posmSetupMapping' + group.type">Mapping JSON</label>
                            </p-iftalabel>
                          </p-inputGroup>
                        </div>
                      }

                      <div class="col-12 flex justify-content-end gap-2">
                        @if (draft.id) {
                          <p-button label="New Value" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="resetDraft(group.type)"></p-button>
                        }
                        <p-button label="Save Value" icon="pi pi-check" [loading]="loading()" (click)="saveSetup(group.type)"></p-button>
                      </div>
                    </div>
                  }

                  <p-table [value]="rows(group.type)" [loading]="loading()" styleClass="p-datatable-sm no-borders" [tableStyle]="{ 'min-width': '900px' }">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Category</th>
                        <th>Version</th>
                        <th>Status</th>
                        <th>Updated</th>
                        <th class="text-right">Action</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td>
                          <div class="font-medium text-900">{{ row.name }}</div>
                          @if (row.description) {
                            <div class="text-xs text-500 mt-1">{{ row.description }}</div>
                          }
                        </td>
                        <td class="text-600">{{ row.code || '-' }}</td>
                        <td class="text-600">{{ row.category || '-' }}</td>
                        <td class="text-600">{{ row.versionNumber || 1 }}</td>
                        <td>
                          <p-tag [value]="row.isActive === false ? 'Inactive' : 'Active'" [severity]="statusSeverity(row)"></p-tag>
                          @if (row.isSystemDefault) {
                            <p-tag value="Default" severity="info" styleClass="ml-2"></p-tag>
                          }
                        </td>
                        <td class="text-600">{{ row.updatedAt ? (row.updatedAt | date:'dd/MM/yyyy') : '-' }}</td>
                        <td class="text-right">
                          <div class="flex justify-content-end gap-2">
                            <p-button icon="pi pi-pencil" severity="secondary" [outlined]="true" (click)="editSetup(row)"></p-button>
                            <p-button
                              [icon]="row.isActive === false ? 'pi pi-check-circle' : 'pi pi-ban'"
                              [severity]="row.isActive === false ? 'success' : 'danger'"
                              [outlined]="true"
                              (click)="toggleActive(row)">
                            </p-button>
                          </div>
                        </td>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                      <tr>
                        <td colspan="7" class="text-center p-4 text-500">No {{ group.title | lowercase }} configured.</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </app-page-data-panel>
              </div>
            }
          </div>
          }
        </p-tabpanel>
      }
    </p-tabpanels>
  </p-tabs>
</app-page-shell>
